/**
 * Model lookup indexes — the by-id maps the inspector and routing read.
 *
 * One recursive walk of the model builds every map the UI needs to answer
 * "find the module / interface / process for this id" in O(1): module by
 * key, parent by key, qualified name by key, interface by qualified name
 * (two views), and process by key. Pulled out of `App` so the build is a
 * pure function of the model and the `ModelIndexes` shape is a stable
 * contract the panels can import directly.
 *
 * Selection-derived lookups (selected module / interface / edge) stay with
 * the selection state; this module is purely structural.
 */

import { useMemo } from "react";
import type {
  ArchitectureModel,
  Interface,
  Module,
  Process,
  StableId,
} from "@archlang/scene/arch-model";
import { flattenInterfaces, moduleKey } from "@archlang/scene/arch-model";
import { processKey } from "../processKey.js";

export interface ModelIndexes {
  readonly moduleById: ReadonlyMap<string, Module>;
  readonly moduleParentById: ReadonlyMap<string, StableId>;
  readonly moduleQualifiedNameById: ReadonlyMap<string, string>;
  readonly interfaceById: ReadonlyMap<string, { iface: Interface; moduleId: StableId }>;
  readonly interfaceByQualifiedName: ReadonlyMap<
    string,
    { moduleId: StableId; interfaceId: string }
  >;
  readonly processById: ReadonlyMap<string, Process>;
  /** Full surface path (`Gateway.Http`, incl. nested + `#id`-key forms) →
   *  owning module. Surfaces aren't separately selectable (the search
   *  index's own law) — an `archlang://` surface address lands on the owner
   *  instead of nowhere (r52). */
  readonly surfaceOwnerByPath: ReadonlyMap<string, StableId>;
}

/** Pure: build every lookup map from a model in one recursive walk. */
export function buildModelIndexes(model: ArchitectureModel): ModelIndexes {
  const moduleById = new Map<string, Module>();
  const moduleParentById = new Map<string, StableId>();
  const moduleQualifiedNameById = new Map<string, string>();
  const interfaceById = new Map<string, { iface: Interface; moduleId: StableId }>();
  const processById = new Map<string, Process>();
  const surfaceOwnerByPath = new Map<string, StableId>();
  const interfaceByQualifiedName = new Map<
    string,
    { moduleId: StableId; interfaceId: string }
  >();

  const visit = (m: Module, parentPath = "", parentKey?: StableId) => {
    const mKey = moduleKey(m);
    moduleById.set(mKey, m);
    if (parentKey) moduleParentById.set(mKey, parentKey);
    const fullPath = parentPath ? `${parentPath}.${m.name}` : m.name;
    moduleQualifiedNameById.set(mKey, fullPath);
    for (const iface of flattenInterfaces(m)) {
      const ifaceKey = `${fullPath}.${iface.name}`;
      interfaceById.set(ifaceKey, { iface, moduleId: mKey });
      interfaceByQualifiedName.set(ifaceKey, { moduleId: mKey, interfaceId: ifaceKey });
      // Alias under the module's STABLE-KEY form too. Surfaces that address an
      // interface as `${moduleKey(m)}.${name}` (the nav tree, board ports)
      // diverge from the name path when the module declares a `#id` — without
      // the alias every lookup for such a module's interfaces misses (owner
      // never selected, inspector blank).
      const idKey = `${mKey}.${iface.name}`;
      if (idKey !== ifaceKey) {
        interfaceById.set(idKey, { iface, moduleId: mKey });
        interfaceByQualifiedName.set(idKey, { moduleId: mKey, interfaceId: ifaceKey });
      }
    }
    // Surface-PATH aliases: the engine addresses surface-nested entities by
    // their full dotted path (`Gateway.Http.read` — description refs,
    // backlinks), while flattenInterfaces elides the surface segment. Alias
    // every full path onto the elided record so an engine-emitted address
    // never misses (r52); nested surfaces recurse.
    const visitSurfaces = (
      surfaces: readonly Module["surfaces"][number][],
      parentPaths: readonly string[],
    ): void => {
      for (const s of surfaces) {
        const sPaths = parentPaths.map((p) => `${p}.${s.name}`);
        for (const sp of sPaths) surfaceOwnerByPath.set(sp, mKey);
        for (const iface of s.interfaces) {
          const elided = `${fullPath}.${iface.name}`;
          for (const sp of sPaths) {
            const full = `${sp}.${iface.name}`;
            if (!interfaceById.has(full)) {
              interfaceById.set(full, { iface, moduleId: mKey });
              interfaceByQualifiedName.set(full, { moduleId: mKey, interfaceId: elided });
            }
          }
        }
        visitSurfaces(s.surfaces, sPaths);
      }
    };
    visitSurfaces(m.surfaces ?? [], mKey !== fullPath ? [fullPath, mKey] : [fullPath]);
    for (const c of m.children) visit(c, fullPath, mKey);
  };
  for (const m of model.modules) visit(m);
  for (const p of model.processes) processById.set(processKey(p), p);

  return {
    moduleById,
    moduleParentById,
    moduleQualifiedNameById,
    interfaceById,
    interfaceByQualifiedName,
    processById,
    surfaceOwnerByPath,
  };
}

/** Memoized lookup indexes, or null until the first model resolves. */
export function useModelIndexes(model: ArchitectureModel | null): ModelIndexes | null {
  return useMemo(() => (model ? buildModelIndexes(model) : null), [model]);
}

/**
 * Walk parent links to a module's dotted qualified name
 * (`Platform.AuthService`). Falls back to the simple name if the parent
 * chain is broken.
 */
export function buildQualifiedName(m: Module, byId: ReadonlyMap<string, Module>): string {
  const mm = m as Module & { parentId?: StableId };
  if (!mm.parentId) return m.name;
  const parent = byId.get(mm.parentId);
  if (!parent) return m.name;
  return `${buildQualifiedName(parent, byId)}.${m.name}`;
}

/**
 * Find a module anywhere in the tree by its qualified path
 * (`Platform.AuthService`) or its simple name (`AuthService`). Accepts
 * either to make deep-links tolerant of how the caller addresses things.
 */
export function findModuleByName(
  modules: readonly Module[],
  name: string,
  parentPath = "",
): Module | null {
  for (const m of modules) {
    const full = parentPath ? `${parentPath}.${m.name}` : m.name;
    if (full === name || m.name === name) return m;
    if (m.children) {
      const nested = findModuleByName(m.children, name, full);
      if (nested) return nested;
    }
  }
  return null;
}
